import { CommandRequest, CommandResult, MessageDetail, MessageStates, Review, ReviewFields } from './types';
import { MessageApiError } from './api';

export type ReviewTab = 'review' | 'evidence' | 'publication' | 'history';
export type FieldGroup = 'basic' | 'facts' | 'impact' | 'ai' | 'reason';
export type VisibilityKind = 'visible-auto' | 'visible-manual' | 'hidden' | 'blocked' | 'source-deleted';
export type PrimaryAction =
  | 'none'
  | 'retry-load'
  | 'view-history'
  | 'view-processing'
  | 'view-evidence'
  | 'save'
  | 'publish'
  | 'publish-update'
  | 'edit-review'
  | 'unblock'
  | 'confirm-result'
  | 'refresh-compare'
  | 'loading';

export interface FieldIssue {
  field: string;
  label: string;
  group: FieldGroup;
  message: string;
  itemIndex?: number;
}

export interface ActionButton {
  id: PrimaryAction | 'withdraw' | 'close' | 'unblock' | 'publish' | 'edit-review' | 'refresh';
  label: string;
  enabled: boolean;
  help: string;
  loading?: boolean;
  danger?: boolean;
  primary?: boolean;
}

export interface ReviewUiState {
  visibilityKind: VisibilityKind;
  visibilityLabel: string;
  progressLabel: string;
  nextStep: string;
  primary: ActionButton;
  secondary: ActionButton[];
  withdraw?: ActionButton;
  showReason: boolean;
  issues: FieldIssue[];
  articleIssues: FieldIssue[];
  missingCount: number;
  defaultTab: ReviewTab;
  canSave: boolean;
  canPublish: boolean;
  canWithdraw: boolean;
  canUnblock: boolean;
  formLocked: boolean;
  analysisV2: boolean;
  compatibleReview: boolean;
  seededFromAnalysis: boolean;
  unpublishedNewReview: boolean;
  publishHelp: string;
  saveHelp: string;
}

export interface ReviewUiInput {
  detail: MessageDetail | null;
  fields: ReviewFields;
  articleDirty: boolean;
  reasonDirty: boolean;
  reviewMismatch: boolean;
  writeBusy: boolean;
  detailLoading: boolean;
  detailError: MessageApiError | null;
  receipt: { contentId: string; result: CommandResult } | null;
  retry: { contentId: string; body: CommandRequest } | null;
  querying: boolean;
  pollTimedOut: boolean;
  attempted: boolean;
  selectedId: string;
  conflictPending: boolean;
  seedSource: 'review' | 'analysis' | 'empty';
  lastSavedReviewId: string | null;
  requireSaveAfterUnblock: boolean;
}

export const importanceLabels: Record<string, string> = {
  GENERAL: '一般',
  IMPORTANT: '重要',
  VERY_IMPORTANT: '非常重要'
};
export const commandTypeLabels: Record<string, string> = {
  PUBLISH: '发布',
  WITHDRAW: '撤回并拦截',
  UNBLOCK: '解除拦截'
};
export const commandStatusLabels: Record<string, string> = {
  PENDING: '处理中',
  COMPLETED: '已完成',
  REJECTED: '已拒绝'
};
export const autoDecisionLabels: Record<string, string> = {
  AUTO_PUBLISH: '自动发布',
  MANUAL_REVIEW: '待人工审核',
  FILTERED: '已过滤',
  BLOCKED: '已拦截',
  STALE: '已过期'
};
export const aiPhaseLabels: Record<string, string> = {
  WAITING_INPUT: '等待有效文本',
  PENDING: '待分析',
  RUNNING: '分析中',
  RETRYING: '重试中',
  FAILED: '分析失败',
  COMPLETED: '已完成'
};
export const aiResultLabels: Record<string, string> = {
  VALID_COMPLETE: '结果有效',
  VALID_WITH_MISSING_FACTS: '存在缺失事实',
  INSUFFICIENT: '信息不足',
  IRRELEVANT: '内容无关'
};
export const reviewStateLabels: Record<string, string> = {
  NOT_READY: '暂无需操作',
  NOT_REQUIRED: '无需审核',
  REQUIRED: '需人工审核',
  REVIEWED: '已审核',
  STALE: '需重新审核'
};
export const publicationStateLabels: Record<string, string> = {
  UNPUBLISHED: '未公开',
  PUBLISHING: '发布中',
  PUBLISHED: '已公开',
  WITHDRAWN: '已撤回',
  BLOCKED: '已拦截',
  FAILED: '发布失败'
};
export const aiPhaseColors: Record<string, string> = {
  WAITING_INPUT: 'gray',
  PENDING: 'orangered',
  RUNNING: 'arcoblue',
  RETRYING: 'gold',
  FAILED: 'red',
  COMPLETED: 'green'
};
export const reviewStateColors: Record<string, string> = {
  NOT_READY: 'gray',
  NOT_REQUIRED: 'gray',
  REQUIRED: 'orangered',
  REVIEWED: 'green',
  STALE: 'gold'
};
export const publicationStateColors: Record<string, string> = {
  UNPUBLISHED: 'gray',
  PUBLISHING: 'arcoblue',
  PUBLISHED: 'green',
  WITHDRAWN: 'gray',
  BLOCKED: 'red',
  FAILED: 'red'
};
export const reasonCodeLabels: Record<string, string> = {
  ELIGIBLE: '符合自动发布条件',
  AI_IRRELEVANT: '内容与产品范围无关',
  ANALYSIS_INSUFFICIENT: '事实不足，无法形成完整结论',
  MISSING_FACTS: '存在缺失事实',
  INCOMPLETE_ANALYSIS: '结构化分析不完整',
  EVIDENCE_NOT_GROUNDED: '证据未落到原文',
  IMPORTANCE_NOT_ENABLED: '重要程度未启用自动发布',
  BACKFILL_REQUIRES_REVIEW: '回填快讯需要人工审核',
  SOURCE_ANALYSIS_MISMATCH: '来源与分析不一致',
  NO_ACTIVE_POLICY: '当前环境没有启用的发布策略，自动发布尚未判定',
  NO_CURRENT_DECISION: '尚未形成当前发布决策',
  TEXT_NOT_READY: '标准化文本尚未就绪',
  ANALYSIS_JOB_NOT_FOUND: '文本已就绪但还没有分析任务',
  ANALYSIS_RESULT_MISSING: '分析任务已成功但缺少结果',
  ANALYSIS_RESULT_INCONSISTENT: '分析结果字段组合不合法',
  ANALYSIS_JOB_INCONSISTENT: '分析任务状态异常',
  REVIEW_STALE: '来源已更新，当前审核已失效'
};

export function aiColumnLabel(ai: MessageStates['ai']): string {
  if (ai.phase === 'COMPLETED') {
    const result = ai.result ? aiResultLabels[ai.result] : null;
    return result ? `已完成 · ${result}` : '已完成';
  }
  return aiPhaseLabels[ai.phase] ?? ai.phase;
}
export function publicationColumnLabel(publication: MessageStates['publication']): string {
  if (publication.state === 'PUBLISHED' && publication.origin === 'AUTOMATIC') return '自动公开';
  if (publication.state === 'PUBLISHED' && publication.origin === 'MANUAL') return '人工公开';
  return publicationStateLabels[publication.state] ?? publication.state;
}
export function reasonHint(code: string | null | undefined): string | null {
  if (!code) return null;
  return reasonCodeLabels[code] ?? `状态异常，请刷新或联系排查（${code}）`;
}

export function displayCode(value: string | null | undefined, labels: Record<string, string>): string {
  if (!value) return '—';
  return labels[value] ?? value;
}

export function statusColor(value: string | null | undefined, colors: Record<string, string>): string {
  return (value && colors[value]) || 'gray';
}

export function codePointLength(value: string): number {
  return Array.from(value).length;
}

export function validText(value: string, max: number): boolean {
  return value.trim().length > 0 && codePointLength(value) <= max;
}

function textIssue(field: string, label: string, group: FieldGroup, value: string, max: number): FieldIssue | null {
  if (!value.trim()) return { field, label, group, message: `${label}不能为空` };
  if (codePointLength(value) > max) return { field, label, group, message: `${label}超出 ${max} 字` };
  return null;
}

function listIssues(field: string, label: string, group: FieldGroup, items: string[]): FieldIssue[] {
  if (items.length === 0) return [{ field, label, group, message: `${label}至少填写 1 项` }];
  if (items.length > 8) return [{ field, label, group, message: `${label}最多 8 项` }];
  return items.flatMap((item, index) => {
    if (!item.trim()) return [{ field, label, group, message: `${label}第${index + 1}项为空`, itemIndex: index }];
    if (codePointLength(item) > 500) {
      return [{ field, label, group, message: `${label}第${index + 1}项超出 500 字`, itemIndex: index }];
    }
    return [];
  });
}

export function collectFieldIssues(fields: ReviewFields): FieldIssue[] {
  const issues: FieldIssue[] = [];
  const push = (issue: FieldIssue | null) => {
    if (issue) issues.push(issue);
  };
  push(textIssue('title', '审核标题', 'basic', fields.title, 500));
  push(textIssue('topic', '主题', 'basic', fields.topic, 64));
  push(textIssue('summary', '摘要', 'basic', fields.summary, 8000));
  push(textIssue('interpretation', '审核解读', 'basic', fields.interpretation, 16000));
  issues.push(...listIssues('keyFacts', '关键事实', 'facts', fields.keyFacts));
  push(textIssue('shortTermImpact', '短期影响', 'impact', fields.shortTermImpact, 2000));
  push(textIssue('mediumTermImpact', '中期影响', 'impact', fields.mediumTermImpact, 2000));
  push(textIssue('actionGuidance', '行动指引', 'impact', fields.actionGuidance, 2000));
  push(textIssue('eventReconstruction', '事件还原', 'ai', fields.eventReconstruction, 4000));
  push(textIssue('confidenceAnalysis', '可信度说明', 'ai', fields.confidenceAnalysis, 2000));
  issues.push(...listIssues('transmissionPath', '传导路径', 'ai', fields.transmissionPath));
  push(textIssue('expectationGap', '预期差', 'ai', fields.expectationGap, 2000));
  issues.push(...listIssues('actionItems', '行动清单', 'ai', fields.actionItems));
  return issues;
}

export function reasonIssue(reason: string): FieldIssue | null {
  return textIssue('reason', '操作原因', 'reason', reason, 2000);
}

export function seedingReview(detail: MessageDetail): Review | null {
  const review = detail.review;
  if (
    review &&
    review.sourceVersion === detail.sourceVersion &&
    review.sourceRevisionId === detail.sourceRevisionId &&
    review.analysisId === detail.analysis?.id
  ) {
    return review;
  }
  return null;
}

export function compatibleReview(detail: MessageDetail): boolean {
  const review = seedingReview(detail);
  return Boolean(review && review.editorialVersion === detail.editorialVersion);
}

export function currentReviewPublished(detail: MessageDetail): boolean {
  const publication = detail.publication;
  const review = detail.review;
  if (!publication?.visible || !review?.id || !publication.reviewId) return false;
  return publication.reviewId === review.id;
}

export function formatBeijingTime(value: string | null | undefined, empty = '未提供'): string {
  if (!value) return empty;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const formatted = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(date);
  return `${formatted}（北京时间）`;
}

export function reviewToFields(review: Review): ReviewFields {
  return {
    title: review.title,
    summary: review.summary,
    interpretation: review.interpretation,
    importance: review.importance,
    featured: review.featured,
    reason: review.reason,
    topic: review.topic ?? '',
    keyFacts: asStringArray(review.keyFacts),
    shortTermImpact: review.shortTermImpact ?? '',
    mediumTermImpact: review.mediumTermImpact ?? '',
    actionGuidance: review.actionGuidance ?? '',
    eventReconstruction: review.eventReconstruction ?? '',
    confidenceAnalysis: review.confidenceAnalysis ?? '',
    transmissionPath: asStringArray(review.transmissionPath),
    expectationGap: review.expectationGap ?? '',
    actionItems: asStringArray(review.actionItems)
  };
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function button(
  id: ActionButton['id'],
  label: string,
  enabled: boolean,
  help: string,
  extra: Partial<ActionButton> = {}
): ActionButton {
  return { id, label, enabled, help, ...extra };
}

function analysisFailed(detail: MessageDetail): boolean {
  const analysis = detail.analysis;
  if (!analysis) return false;
  return analysis.status === 'FAILED' || analysis.validity === 'INVALID';
}

function processingInFlight(detail: MessageDetail): boolean {
  return detail.processing.some(job => job.status === 'READY' || job.status === 'RUNNING' || job.status === 'RETRY_WAIT');
}

function pendingCommandOf(detail: MessageDetail | null, receipt: CommandResult | null) {
  if (receipt?.status === 'PENDING') return receipt;
  return detail?.commands.find(command => command.status === 'PENDING') ?? null;
}

export function deriveReviewUiState(input: ReviewUiInput): ReviewUiState {
  const {
    detail,
    fields,
    articleDirty,
    reviewMismatch,
    writeBusy,
    detailLoading,
    detailError,
    receipt,
    retry,
    querying,
    pollTimedOut,
    attempted,
    selectedId,
    conflictPending,
    seedSource,
    lastSavedReviewId,
    requireSaveAfterUnblock
  } = input;
  const analysisV2 = detail?.analysis?.articleSchemaVersion === '2';
  const articleIssues = detail && analysisV2 ? collectFieldIssues(fields) : [];
  const reason = reasonIssue(fields.reason);
  const issues = attempted ? [...articleIssues, ...(reason ? [reason] : [])] : [];
  const missingCount = articleIssues.length + (reason ? 1 : 0);
  const readOnly = Boolean(detail && !detail.capabilities.canWrite);
  const ownRetry = retry && retry.contentId === selectedId ? retry : null;
  const ownReceipt = receipt && receipt.contentId === selectedId ? receipt : null;
  const pendingCommand = pendingCommandOf(detail, ownReceipt?.result ?? null);
  const formLocked = readOnly || writeBusy || Boolean(ownRetry) || Boolean(pendingCommand);
  const compatible = Boolean(detail && compatibleReview(detail));
  const visible = Boolean(detail?.publication?.visible);
  const blocked = Boolean(detail?.manualBlock);
  const validArticle = Boolean(analysisV2 && articleIssues.length === 0);
  const validReason = !reason;
  const canSave = Boolean(
    detail &&
      !formLocked &&
      !detail.sourceDeleted &&
      detail.text?.status === 'READY' &&
      detail.analysis &&
      analysisV2 &&
      validArticle &&
      validReason
  );
  const unpublishedNewReview = Boolean(
    detail &&
      compatible &&
      !articleDirty &&
      !reviewMismatch &&
      detail.review &&
      detail.publication?.reviewId !== detail.review.id &&
      (detail.publication?.reviewId != null || lastSavedReviewId === detail.review.id)
  );
  const canPublish = Boolean(
    detail &&
      !formLocked &&
      compatible &&
      !articleDirty &&
      !reviewMismatch &&
      !blocked &&
      !detail.sourceDeleted &&
      validReason &&
      !requireSaveAfterUnblock &&
      (!visible || unpublishedNewReview)
  );
  const canWithdraw = Boolean(detail && !readOnly && !writeBusy && !ownRetry && !pendingCommand && !blocked && !detail.sourceDeleted);
  const canUnblock = Boolean(
    detail && !readOnly && !writeBusy && !ownRetry && !pendingCommand && blocked && validReason && !detail.sourceDeleted
  );
  const seededFromAnalysis = seedSource === 'analysis';
  const visibilityKind: VisibilityKind = detail?.sourceDeleted
    ? 'source-deleted'
    : blocked
      ? 'blocked'
      : visible
        ? detail?.publication?.origin === 'AUTOMATIC'
          ? 'visible-auto'
          : 'visible-manual'
        : 'hidden';
  const visibilityLabel =
    visibilityKind === 'source-deleted'
      ? '来源已删除'
      : visibilityKind === 'blocked'
        ? '已拦截 · 当前不可见'
        : visibilityKind === 'visible-auto'
          ? '已公开 · 自动发布'
          : visibilityKind === 'visible-manual'
            ? '已公开 · 人工发布'
            : '当前未公开';

  let progressLabel = '';
  if (articleDirty) progressLabel = '有修改未保存';
  else if (requireSaveAfterUnblock) progressLabel = '拦截已解除，尚未公开';
  else if (unpublishedNewReview && visible) progressLabel = '新审核已保存，尚未发布更新';
  else if (compatible && !visible && !blocked && !reviewMismatch && !articleDirty) progressLabel = '审核已保存，尚未公开';
  else if (seededFromAnalysis) progressLabel = '内容来源：当前 AI 分析';
  else if (compatible) progressLabel = '内容来源：已保存审核';

  const saveHelp = saveHelpText(canSave, missingCount, reason, blocked, visible);
  const publishHelp = canPublish
    ? visible && unpublishedNewReview
      ? '可以执行：发布更新后替换用户当前看到的版本。'
      : '可以执行：使用当前已保存的审核版本公开。'
    : readOnly
      ? '当前账号只读'
      : writeBusy
        ? '正在处理，请稍候'
        : detailLoading && !detail
          ? '正在刷新详情'
          : detailError && !detail
            ? '详情加载失败，请先重试'
            : ownRetry
              ? '上次命令结果待确认，请先确认本次操作结果'
              : pendingCommand
                ? '正在等待处理结果'
                : detail?.sourceDeleted
                  ? '来源已删除，不能发布'
                  : blocked
                    ? '快讯已拦截，请先解除拦截'
                    : articleDirty || reviewMismatch || !compatible || requireSaveAfterUnblock
                      ? '请先保存审核'
                      : !validReason
                        ? '填写操作原因后可发布'
                        : '当前不可发布';

  const base: ReviewUiState = {
    visibilityKind,
    visibilityLabel,
    progressLabel,
    nextStep: '',
    primary: button('none', '', false, ''),
    secondary: [],
    showReason: !readOnly,
    issues,
    articleIssues,
    missingCount,
    defaultTab: 'review',
    canSave,
    canPublish,
    canWithdraw,
    canUnblock,
    formLocked,
    analysisV2: Boolean(analysisV2),
    compatibleReview: compatible,
    seededFromAnalysis,
    unpublishedNewReview,
    publishHelp,
    saveHelp
  };

  const withPrimary = (state: Partial<ReviewUiState> & Pick<ReviewUiState, 'nextStep' | 'primary'>): ReviewUiState => ({
    ...base,
    ...state,
    withdraw:
      canWithdraw && !readOnly
        ? button(
            'withdraw',
            visible ? '撤回并拦截' : '拦截发布',
            true,
            visible ? '用户将无法再看到这条快讯，同时阻止自动发布' : '拦截后不会公开，并阻止自动发布',
            { danger: true }
          )
        : undefined
  });

  if (detailLoading && !detail) {
    return withPrimary({
      nextStep: '正在加载快讯',
      primary: button('loading', '加载中', false, '正在加载快讯', { loading: true }),
      showReason: false,
      defaultTab: 'review',
      progressLabel: '正在加载'
    });
  }
  if (detailError && !detail) {
    return withPrimary({
      nextStep: '详情加载失败，重试后继续',
      primary: button('retry-load', '重试加载', true, '重新获取这条快讯的详情'),
      showReason: false,
      defaultTab: 'evidence',
      progressLabel: '详情加载失败'
    });
  }
  if (!detail) {
    return withPrimary({
      nextStep: '选择一条快讯开始审核',
      primary: button('none', '', false, ''),
      showReason: false
    });
  }
  if (readOnly) {
    return withPrimary({
      nextStep: '当前账号仅可查看，审核和发布需要编辑权限',
      primary: button('none', '', false, '当前账号只读'),
      showReason: false,
      defaultTab: visible ? 'publication' : 'evidence',
      progressLabel: '只读查看'
    });
  }
  if (detail.sourceDeleted) {
    return withPrimary({
      nextStep: '来源已删除，当前不可公开；可查看历史记录',
      primary: button('view-history', '查看记录', true, '打开操作记录'),
      showReason: false,
      defaultTab: 'history',
      progressLabel: '来源已删除'
    });
  }
  if (ownRetry) {
    return withPrimary({
      nextStep: '操作结果待确认，请先确认本次操作结果',
      primary: button('confirm-result', '确认操作结果', !writeBusy, '查询或重送原命令，不使用当前表单重新组装'),
      progressLabel: '操作结果待确认',
      defaultTab: 'review'
    });
  }
  if (conflictPending) {
    return withPrimary({
      nextStep: '内容已更新，你的编辑已保留。先核对最新版本。',
      primary: button('refresh-compare', '刷新并核对', !writeBusy, '获取最新审核并对照当前编辑'),
      progressLabel: '版本已变化',
      defaultTab: 'review'
    });
  }
  if (writeBusy) {
    const saving = !ownReceipt;
    return withPrimary({
      nextStep: saving ? '正在保存审核' : '正在提交发布申请',
      primary: button(saving ? 'save' : 'publish', saving ? '正在保存审核' : '正在提交', false, '当前快讯写操作进行中', {
        loading: true,
        primary: true
      }),
      progressLabel: saving ? '正在保存审核' : '正在提交',
      showReason: true
    });
  }
  if (pendingCommand) {
    const withdrawSettled = detail.manualBlock && !visible;
    return withPrimary({
      nextStep: pollTimedOut
        ? '仍在处理，可以稍后回来查看'
        : withdrawSettled
          ? '已拦截，用户已不可见；处理记录仍在完成中'
          : '发布申请已提交，正在等待处理',
      primary: button('loading', '处理中', false, querying ? '正在查询处理结果' : '等待处理完成', { loading: true }),
      progressLabel: pollTimedOut ? '仍在处理' : withdrawSettled ? '已拦截，处理仍在完成' : '发布申请已提交，正在等待处理',
      defaultTab: 'review'
    });
  }
  if (detail.text?.status !== 'READY') {
    return withPrimary({
      nextStep: processingInFlight(detail) ? '原文尚未准备好，当前不能审核' : '原文尚未准备好，当前不能审核',
      primary: button('view-processing', '查看处理状态', true, '打开原文与处理记录'),
      defaultTab: 'evidence',
      showReason: canWithdraw || canUnblock,
      progressLabel: '原文未就绪'
    });
  }
  if (!detail.analysis) {
    return withPrimary({
      nextStep: '暂无分析结果',
      primary: button('view-processing', '查看处理状态', true, '打开原文与处理记录'),
      defaultTab: 'evidence',
      showReason: canWithdraw || canUnblock,
      progressLabel: '暂无分析'
    });
  }
  if (analysisFailed(detail)) {
    return withPrimary({
      nextStep: '分析失败，需处理后才能审核',
      primary: button('view-processing', '查看处理状态', true, '打开原文与处理记录'),
      defaultTab: 'evidence',
      showReason: canWithdraw || canUnblock,
      progressLabel: '分析失败'
    });
  }
  if (!analysisV2) {
    return withPrimary({
      nextStep: '当前分析缺少结构化字段，不能保存审核',
      primary: button('view-evidence', '查看原文与分析', true, '打开原文与 AI 依据'),
      defaultTab: 'evidence',
      showReason: canWithdraw || canUnblock,
      progressLabel: '分析不完整'
    });
  }
  if (requireSaveAfterUnblock && !articleDirty) {
    return withPrimary({
      nextStep: '拦截已解除，当前未公开；请重新保存审核，再提交发布',
      primary: button('save', '保存审核', canSave, saveHelp, { primary: true }),
      defaultTab: 'review',
      showReason: true,
      progressLabel: '拦截已解除，尚未公开'
    });
  }
  if (articleDirty) {
    return withPrimary({
      nextStep: visible
        ? '保存审核。保存后，用户仍看到当前公开版本。'
        : blocked
          ? '当前已拦截，可保存修改；发布前还需解除拦截'
          : '保存审核。保存不会发布',
      primary: button('save', '保存审核', canSave, saveHelp, { primary: true }),
      secondary: blocked ? [button('unblock', '解除拦截', canUnblock, unblockHelp(canUnblock, validReason))] : [],
      defaultTab: 'review',
      showReason: true,
      progressLabel: '有修改未保存'
    });
  }
  if (blocked) {
    return withPrimary({
      nextStep: '当前已拦截；解除后仍不会自动公开',
      primary: button('unblock', '解除拦截', canUnblock, unblockHelp(canUnblock, validReason), { primary: true }),
      secondary: [button('edit-review', '编辑审核内容', true, '进入审核页签，不发送请求')],
      defaultTab: 'review',
      showReason: true,
      progressLabel: '已拦截'
    });
  }
  if (visible && unpublishedNewReview) {
    return withPrimary({
      nextStep: '新版审核已保存，用户仍看到原版本；发布更新后替换',
      primary: button('publish-update', '发布更新', canPublish, publishHelp, { primary: true }),
      defaultTab: 'review',
      showReason: true,
      progressLabel: '新审核已保存，尚未发布更新'
    });
  }
  if (visible) {
    return withPrimary({
      nextStep: '当前已公开，无需操作；需要修改时点击编辑审核内容',
      primary: button('edit-review', '编辑审核内容', true, '进入审核页签，不发送请求', { primary: true }),
      defaultTab: 'publication',
      showReason: false,
      progressLabel: detail.publication?.origin === 'AUTOMATIC' ? '已公开 · 自动发布' : '当前已公开，无需处理'
    });
  }
  if (compatible && !reviewMismatch) {
    return withPrimary({
      nextStep: '审核已保存，可提交发布',
      primary: button('publish', '提交发布', canPublish, publishHelp, { primary: true }),
      defaultTab: 'review',
      showReason: true
    });
  }
  return withPrimary({
    nextStep: '保存审核。保存不会发布',
    primary: button('save', '保存审核', canSave, saveHelp, { primary: true }),
    defaultTab: 'review',
    showReason: true,
    progressLabel: seededFromAnalysis ? '内容来源：当前 AI 分析' : '尚未保存审核'
  });
}

function saveHelpText(
  canSave: boolean,
  missingCount: number,
  reason: FieldIssue | null,
  blocked: boolean,
  visible: boolean
): string {
  if (canSave) {
    return visible
      ? '可以执行：仅保存审核内容，不会发布。用户仍看到当前公开版本。'
      : blocked
        ? '可以执行：仅保存审核内容；发布前还需解除拦截。'
        : '可以执行：仅保存审核内容，不会发布。';
  }
  if (reason && missingCount === 1) return '填写操作原因后可保存';
  if (missingCount > 0) return `还需补全 ${missingCount} 处`;
  return '当前不能保存';
}

function unblockHelp(canUnblock: boolean, validReason: boolean): string {
  if (canUnblock) return '可以执行：仅解除拦截，不会自动恢复发布。';
  if (!validReason) return '填写操作原因后可解除拦截';
  return '当前不可解除拦截';
}

export function groupMissingCount(issues: FieldIssue[], group: FieldGroup): number {
  return issues.filter(issue => issue.group === group).length;
}

export function fieldDiffs(
  current: ReviewFields,
  latest: ReviewFields
): { field: string; label: string; current: string; latest: string }[] {
  const rows: { key: keyof ReviewFields; label: string }[] = [
    { key: 'title', label: '审核标题' },
    { key: 'topic', label: '主题' },
    { key: 'importance', label: '重要程度' },
    { key: 'featured', label: '精选' },
    { key: 'summary', label: '摘要' },
    { key: 'interpretation', label: '审核解读' },
    { key: 'keyFacts', label: '关键事实' },
    { key: 'shortTermImpact', label: '短期影响' },
    { key: 'mediumTermImpact', label: '中期影响' },
    { key: 'actionGuidance', label: '行动指引' },
    { key: 'eventReconstruction', label: '事件还原' },
    { key: 'confidenceAnalysis', label: '可信度说明' },
    { key: 'transmissionPath', label: '传导路径' },
    { key: 'expectationGap', label: '预期差' },
    { key: 'actionItems', label: '行动清单' }
  ];
  return rows.flatMap(({ key, label }) => {
    const left = formatValue(current[key]);
    const right = formatValue(latest[key]);
    return left === right ? [] : [{ field: String(key), label, current: left, latest: right }];
  });
}

function formatValue(value: ReviewFields[keyof ReviewFields]): string {
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (Array.isArray(value)) return value.join('\n');
  return String(value ?? '');
}
